import React, { useState, useRef, useCallback } from "react";
import "./Analytics.css";
import assistantService from "../services/assistantService";

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const getYesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const AnalyticsInsight = ({ year, month, week }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("week"); // "day" | "week" | "month"
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  const fetchTab = useCallback(async (t) => {
    if (cache[t] || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      let result;
      if (t === "day")   result = await assistantService.getDailyInsight(getYesterdayStr());
      else if (t === "week")  result = await assistantService.getWeeklyInsight(year, month, week);
      else                    result = await assistantService.getMonthlyInsight(year, month);
      setCache(prev => ({ ...prev, [t]: result }));
    } catch {
      setError("Couldn't generate insight. Try again later.");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [cache, year, month, week]);

  const handleToggle = () => {
    const opening = !open;
    setOpen(opening);
    if (opening) fetchTab(tab);
  };

  const handleTab = (t) => {
    setTab(t);
    setError(null);
    if (open) fetchTab(t);
  };

  const data = cache[tab];

  const tabLabels = { day: "Yesterday", week: "This Week", month: "This Month" };

  return (
    <div className="analytics-insight-wrapper">
      <div className="analytics-insight-toggle-row">
        <button
          className={`analytics-insight-toggle ${open ? "open" : ""}`}
          onClick={handleToggle}
        >
          <span className="ai-insight-star">✦</span>
          <span>AI Insight</span>
          <span className="ai-insight-badge">Powered by Groq</span>
        </button>
        <div className="ai-tab-group">
          {["day", "week", "month"].map(t => (
            <button
              key={t}
              className={`ai-tab-btn ${tab === t ? "active" : ""}`}
              onClick={() => handleTab(t)}
            >
              {tabLabels[t]}
            </button>
          ))}
        </div>
        <span className="ai-insight-chevron" onClick={handleToggle}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="analytics-insight-panel">
          {loading && (
            <div className="analytics-insight-loading">
              <span className="ai-spinner" />
              <span>Analyzing {tabLabels[tab].toLowerCase()} data...</span>
            </div>
          )}
          {error && !loading && <p className="analytics-insight-error">{error}</p>}
          {data && !loading && (
            <>
              {data.stats && (
                <div className="analytics-insight-pills">
                  <span className={`ai-pill ${
                    data.stats.completionRate >= 80 ? "ai-pill-green"
                    : data.stats.completionRate >= 50 ? "ai-pill-amber"
                    : "ai-pill-red"
                  }`}>
                    {data.stats.completed}/{data.stats.total} done
                  </span>
                  {data.stats.completionRate !== undefined && (
                    <span className="ai-pill ai-pill-neutral">{data.stats.completionRate}% rate</span>
                  )}
                  {data.stats.timeSpent && data.stats.timeSpent !== "0m" && (
                    <span className="ai-pill ai-pill-blue">⏱ {data.stats.timeSpent} tracked</span>
                  )}
                  {data.stats.missed > 0 && (
                    <span className="ai-pill ai-pill-red">⚠ {data.stats.missed} missed</span>
                  )}
                </div>
              )}
              <p className="analytics-insight-text">{data.insight}</p>
            </>
          )}
          {!data && !loading && !error && (
            <p className="analytics-insight-loading" style={{color:'var(--text-muted)'}}>
              Select a tab above and open to generate insight.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const Analytics = ({ analytics, type, todos = [], missedTodos = [], year, month, week }) => {
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const totalTodos = todos.length;
  const completedTodos = todos.filter(todo => todo.completed).length;
  // Deduplicate: include todos from the prop + any missed ones in the todos list
  const allMissedTodos = [
    ...todos.filter(t => t.missed && !t.completed),
    ...missedTodos.filter(t => !todos.find(td => td._id === t._id)),
  ];
  const missedTasksCount = analytics?.missedTasks || 0;
  const missedItems = analytics?.missedItems || [];


  const formatPercentage = (value, total) => {
    if (total === 0) return "0%";
    return `${Math.round((value / total) * 100)}%`;
  };

  const [showMissedReview, setShowMissedReview] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  if (!analytics) {
    return <div className="analytics-loading">Loading analytics...</div>;
  }

  // Prepare recharts data
  const trendData = analytics.byDay
    ? Object.entries(analytics.byDay).map(([day, data]) => {
        const actualMins = Math.round(data.totalTime / 60000);
        const plannedMins = Math.round((data.plannedTime || 0) / 60000);
        
        let productivity = 0;
        if (plannedMins > 0) {
          productivity = Math.round((actualMins / plannedMins) * 100);
        } else if (actualMins > 0) {
          productivity = 100; // Unplanned work
        }

        return {
          name: day.substring(0, 3).toUpperCase(),
          Productivity: productivity,
          Actual: actualMins,
          Target: plannedMins
        };
      })
    : [];

  // Missed Items Review section
  const hasMissed = missedItems.length > 0 || allMissedTodos.length > 0;
  const byDay = {};
  missedItems.forEach(item => {
    const day = item.day || "unknown";
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(item);
  });

  const MissedReviewSection = () => {
    if (!hasMissed) return null;
    return (
      <div className="analytics-section missed-review-section">
        <div
          className="missed-review-header"
          onClick={() => setShowMissedReview(s => !s)}
        >
          <h4 style={{ margin: 0 }}>
            <span style={{ marginRight: 6 }}>⚠️</span>
            Missed Items Review
            <span className="missed-review-count">{missedItems.length + allMissedTodos.length}</span>
          </h4>
          <span className="missed-review-toggle">{showMissedReview ? "▲" : "▼"}</span>
        </div>
        {showMissedReview && (
          <div className="missed-review-body">
            {Object.keys(byDay).length > 0 && (
              <div className="missed-tasks-group">
                <div className="missed-group-label">Tasks</div>
                {Object.entries(byDay).map(([day, items]) => (
                  <div key={day}>
                    <div className="missed-day-label">{day.charAt(0).toUpperCase() + day.slice(1)}</div>
                    {items.map(item => (
                      <div key={item._id} className="missed-item-row">
                        <span className="missed-item-category">{item.category}</span>
                        <span className="missed-item-name">{item.name}</span>
                        {item.missedAt && (
                          <span className="missed-item-time">
                            {new Date(item.missedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {allMissedTodos.length > 0 && (
              <div className="missed-tasks-group">
                <div className="missed-group-label">Quick Todos</div>
                {allMissedTodos.map(todo => (
                  <div key={todo._id} className="missed-item-row">
                    <span className="missed-item-todo-icon">✓</span>
                    <span className="missed-item-name">{todo.text}</span>
                    {todo.deadline && (
                      <span className="missed-item-time">
                        Due: {new Date(todo.deadline + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="analytics-container">
      {type === "week" && (
        <>
          <div className="analytics-summary">
            <div className="stat-card">
              <div className="stat-value">{analytics.totalTasks}</div>
              <div className="stat-label">Total Tasks</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{analytics.sessionCount}</div>
              <div className="stat-label">Sessions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {formatTime(analytics.totalTime)}
              </div>
              <div className="stat-label">Actual Time</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {formatTime(analytics.totalPlannedTime || 0)}
              </div>
              <div className="stat-label">Planned Time</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{completedTodos}/{totalTodos}</div>
              <div className="stat-label">Quick Todos</div>
            </div>
            <div className="stat-card stat-card-missed">
              <div className="stat-value missed-stat-value">{missedTasksCount}</div>
              <div className="stat-label">Missed Tasks</div>
            </div>
            <div className="stat-card stat-card-missed">
              <div className="stat-value missed-stat-value">{allMissedTodos.length}</div>
              <div className="stat-label">Missed Todos</div>
            </div>
          </div>

          <div className="analytics-charts-grid">
            <div className="analytics-section">
              <h4>Productivity Trend</h4>
              <div className="chart-container" style={{ height: 280, marginTop: 20 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barCategoryGap="10%">
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#7f756e' }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#7f756e' }} tickFormatter={(val) => `${val}%`} />
                    <Tooltip
                      cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e1e3e4', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      formatter={(value, name) => {
                        if (name === 'Productivity') return [`${value}%`, name];
                        return [`${value} min`, name];
                      }}
                    />
                    <Bar dataKey="Productivity" fill="#191c1d" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="analytics-section">
              <h4>Category Split</h4>
              <div className="pie-chart-container">
                <div className="pie-chart-wrapper">
                  <svg
                    viewBox="0 0 200 200"
                    className="pie-chart"
                    width="260"
                    height="260"
                  >
                    {(() => {
                      if (
                        !analytics.byCategory ||
                        Object.keys(analytics.byCategory).length === 0
                      ) {
                        return (
                          <>
                            <circle cx="100" cy="100" r="80" fill="#e5e7eb" />
                            <text
                              x="100"
                              y="105"
                              textAnchor="middle"
                              fill="#64748b"
                              fontSize="14"
                            >
                              No data
                            </text>
                          </>
                        );
                      }

                      const categoryData = Object.entries(analytics.byCategory);
                      const total = categoryData.reduce(
                        (sum, [, data]) => sum + data.totalTime,
                        0
                      );

                      if (total === 0) {
                        return (
                          <>
                            <circle cx="100" cy="100" r="80" fill="#e5e7eb" />
                            <text
                              x="100"
                              y="105"
                              textAnchor="middle"
                              fill="#64748b"
                              fontSize="14"
                            >
                              No time logged
                            </text>
                          </>
                        );
                      }

                      const colors = [
                        "#191c1d",
                        "#685c53",
                        "#9e9e9e",
                        "#d0c4bc",
                        "#4d453f",
                        "#b8b0a8",
                      ];
                      let cumulativeAngle = 0;

                      return categoryData.map(([category, data], index) => {
                        const percentageValue = (data.totalTime / total) * 100;
                        const angle = (percentageValue / 100) * 360;
                        const startAngle = cumulativeAngle - 90;
                        const endAngle = startAngle + angle;

                        const startRadians = (startAngle * Math.PI) / 180;
                        const endRadians = (endAngle * Math.PI) / 180;
                        const midAngle = startAngle + angle / 2;
                        const midRadians = (midAngle * Math.PI) / 180;

                        const radius = 80;
                        const x1 = 100 + radius * Math.cos(startRadians);
                        const y1 = 100 + radius * Math.sin(startRadians);
                        const x2 = 100 + radius * Math.cos(endRadians);
                        const y2 = 100 + radius * Math.sin(endRadians);

                        // Calculate offset for pop-out effect
                        const isHovered = hoveredCategory === category;
                        const offset = isHovered ? 8 : 0;
                        const offsetX = offset * Math.cos(midRadians);
                        const offsetY = offset * Math.sin(midRadians);

                        // Label position
                        const labelRadius = radius * 0.7;
                        const lx = 100 + labelRadius * Math.cos(midRadians) + offsetX;
                        const ly = 100 + labelRadius * Math.sin(midRadians) + offsetY;

                        const largeArc = angle > 180 ? 1 : 0;
                        cumulativeAngle += angle;

                        return (
                          <g key={category}>
                            <path
                              d={`M 100 100 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                              fill={colors[index % colors.length]}
                              style={{ 
                                transform: `translate(${offsetX}px, ${offsetY}px)`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                cursor: 'pointer',
                                filter: isHovered ? 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' : 'none'
                              }}
                              onMouseEnter={() => setHoveredCategory(category)}
                              onMouseLeave={() => setHoveredCategory(null)}
                            />
                            {percentageValue > 5 && (
                              <text
                                x={lx}
                                y={ly}
                                textAnchor="middle"
                                alignmentBaseline="middle"
                                fill="#fff"
                                fontSize="10"
                                fontWeight="700"
                                style={{ 
                                  pointerEvents: 'none',
                                  opacity: isHovered ? 1 : 0.8,
                                  transition: 'all 0.3s'
                                }}
                              >
                                {Math.round(percentageValue)}%
                              </text>
                            )}
                          </g>
                        );
                      });
                  })()}
                  {/* Donut center cutout */}
                  <circle cx="100" cy="100" r="55" fill="var(--bg-card, #ffffff)" />
                  {analytics.byCategory && (() => {
                    const total = Object.values(analytics.byCategory).reduce((sum, d) => sum + d.totalTime, 0);
                    return total > 0 ? (
                      <>
                        <text x="100" y="96" textAnchor="middle" fill="var(--text-primary, #191c1d)" fontSize="16" fontWeight="700">
                          {formatTime(total)}
                        </text>
                        <text x="100" y="112" textAnchor="middle" fill="var(--text-muted, #7f756e)" fontSize="9">
                          Total
                        </text>
                      </>
                    ) : null;
                  })()}
                </svg>
              </div>
              <div className="pie-legend">
                {analytics.byCategory &&
                    Object.entries(analytics.byCategory).map(
                      ([category, data], index) => {
                        const colors = [
                          "#191c1d",
                          "#685c53",
                          "#9e9e9e",
                          "#d0c4bc",
                          "#4d453f",
                          "#b8b0a8",
                        ];
                        const total = Object.values(
                          analytics.byCategory
                        ).reduce((sum, d) => sum + d.totalTime, 0);
                        const percentage =
                          total > 0
                            ? Math.round((data.totalTime / total) * 100)
                            : 0;

                        return (
                          <div 
                            key={category} 
                            className={`legend-item ${hoveredCategory === category ? 'active' : ''}`}
                            onMouseEnter={() => setHoveredCategory(category)}
                            onMouseLeave={() => setHoveredCategory(null)}
                            style={{ 
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              transform: hoveredCategory === category ? 'translateX(5px)' : 'none'
                            }}
                          >
                            <span
                              className="legend-color"
                              style={{
                                background: colors[index % colors.length],
                              }}
                            ></span>
                            <span className="legend-label">
                              {category}: {formatTime(data.totalTime)} (
                              {percentage}%)
                            </span>
                          </div>
                        );
                      }
                    )}
                </div>
              </div>
            </div>
          </div>

          <div className="analytics-section">
            <h4>Category Breakdown</h4>
            <div className="category-breakdown">
              {analytics.byCategory &&
                Object.entries(analytics.byCategory).map(([category, data]) => (
                  <div key={category} className="category-stat">
                    <div className="category-info">
                      <span className="category-name-analytics">
                        {category}
                      </span>
                      <span className="category-tasks">
                        {data.taskCount} tasks
                      </span>
                    </div>
                    <div className="category-bar-container">
                      <div
                        className="category-bar"
                        style={{
                          width: formatPercentage(
                            data.totalTime,
                            analytics.totalTime
                          ),
                          background: "var(--accent-primary, #000)",
                        }}
                      />
                    </div>
                    <div className="category-time">
                      {formatTime(data.totalTime)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <MissedReviewSection />
          <AnalyticsInsight year={year} month={month} week={week} />
        </>
      )}
      


      {type === "month" && (
        <>
          <div className="analytics-summary">
            <div className="stat-card">
              <div className="stat-value">{analytics.totalTasks}</div>
              <div className="stat-label">Total Tasks</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{analytics.sessionCount}</div>
              <div className="stat-label">Total Sessions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {formatTime(analytics.totalTime)}
              </div>
              <div className="stat-label">Total Time</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{analytics.completedTasks}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-card stat-card-missed">
              <div className="stat-value missed-stat-value">{analytics.missedTasks || 0}</div>
              <div className="stat-label">Missed Tasks</div>
            </div>
          </div>

          <div className="analytics-section">
            <h4>Weekly Overview</h4>
            <div className="week-comparison">
              {analytics.byWeek &&
                Object.entries(analytics.byWeek).map(([week, data]) => (
                  <div key={week} className="week-stat">
                    <div className="week-label">
                      {week.replace("week", "Week ")}
                    </div>
                    <div className="week-bar-container">
                      <div
                        className="week-bar"
                        style={{
                          width: formatPercentage(
                            data.totalTime,
                            analytics.totalTime
                          ),
                          background: "var(--accent-primary, #000)",
                        }}
                      />
                    </div>
                    <div className="week-details">
                      <span>{formatTime(data.totalTime)}</span>
                      <span>{data.taskCount} tasks</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="analytics-section">
            <h4>Category Breakdown</h4>
            <div className="category-breakdown">
              {analytics.byCategory &&
                Object.entries(analytics.byCategory).map(([category, data]) => (
                  <div key={category} className="category-stat">
                    <div className="category-info">
                      <span className="category-name-analytics">
                        {category}
                      </span>
                      <span className="category-tasks">
                        {data.taskCount} tasks
                      </span>
                    </div>
                    <div className="category-bar-container">
                      <div
                        className="category-bar"
                        style={{
                          width: formatPercentage(
                            data.totalTime,
                            analytics.totalTime
                          ),
                          background: "var(--accent-primary, #000)",
                        }}
                      />
                    </div>
                    <div className="category-time">
                      {formatTime(data.totalTime)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <div className="analytics-section missed-summary-month">
          </div>
          <AnalyticsInsight year={year} month={month} week={week} />
        </>
      )}

      {type === "category" && (
        <>
          <div className="analytics-summary">
            <div className="stat-card">
              <div className="stat-value">{analytics.totalTasks}</div>
              <div className="stat-label">Total Tasks</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{analytics.totalSessions}</div>
              <div className="stat-label">Total Sessions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {formatTime(analytics.totalTime)}
              </div>
              <div className="stat-label">Actual Time</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {formatTime(analytics.totalPlannedTime || 0)}
              </div>
              <div className="stat-label">Planned Time</div>
            </div>
          </div>

          <div className="analytics-section">
            <h4>Time Comparison</h4>
            <div className="pie-chart-container">
              <div className="pie-chart-wrapper">
                <svg
                  viewBox="0 0 200 200"
                  className="pie-chart"
                  width="300"
                  height="300"
                >
                  {(() => {
                    const actualTime = analytics.totalTime;
                    const plannedTime = analytics.totalPlannedTime || 0;

                    if (plannedTime === 0) {
                      return (
                        <>
                          <circle cx="100" cy="100" r="80" fill="var(--accent-primary, #191c1d)" />
                          <text
                            x="100"
                            y="105"
                            textAnchor="middle"
                            fill="#fff"
                            fontSize="14"
                            fontWeight="600"
                          >
                            No planned time
                          </text>
                        </>
                      );
                    }

                    const percentage = Math.min(
                      (actualTime / plannedTime) * 100,
                      100
                    );
                    const angle = (percentage / 100) * 360;
                    const radians = (angle - 90) * (Math.PI / 180);

                    const x = 100 + 80 * Math.cos(radians);
                    const y = 100 + 80 * Math.sin(radians);

                    const largeArc = angle > 180 ? 1 : 0;

                    return (
                      <>
                        {/* Background circle (planned) */}
                        <circle cx="100" cy="100" r="80" fill="#e5e7eb" />

                        {/* Actual time arc */}
                        {percentage > 0 && (
                          <path
                            d={`M 100 20 A 80 80 0 ${largeArc} 1 ${x} ${y} L 100 100 Z`}
                            fill="var(--accent-primary, #191c1d)"
                          />
                        )}

                        {/* Center text */}
                        <text
                          x="100"
                          y="95"
                          textAnchor="middle"
                          fill="#334155"
                          fontSize="24"
                          fontWeight="700"
                        >
                          {Math.round(percentage)}%
                        </text>
                        <text
                          x="100"
                          y="115"
                          textAnchor="middle"
                          fill="#64748b"
                          fontSize="12"
                        >
                          Complete
                        </text>
                      </>
                    );
                  })()}
                </svg>
                <div className="pie-legend">
                  <div className="legend-item">
                    <span
                      className="legend-color"
                      style={{ background: "var(--accent-primary, #191c1d)" }}
                    ></span>
                    <span className="legend-label">
                      Actual: {formatTime(analytics.totalTime)}(
                      {Math.round(
                        (analytics.totalTime /
                          (analytics.totalPlannedTime || 1)) *
                          100
                      )}
                      %)
                    </span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="legend-color"
                      style={{ background: "#e5e7eb" }}
                    ></span>
                    <span className="legend-label">
                      Planned: {formatTime(analytics.totalPlannedTime || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="analytics-section">
            <h4>Activity Timeline</h4>
            <div className="date-timeline">
              {Object.entries(analytics.byDate || {}).map(([date, data]) => (
                <div key={date} className="date-stat">
                  <div className="date-label">
                    {new Date(date).toLocaleDateString()}
                  </div>
                  <div className="date-bar-container">
                    <div
                      className="date-bar"
                      style={{
                        width: formatPercentage(
                          data.totalTime,
                          analytics.totalTime
                        ),
                        background: "var(--accent-primary, #000)",
                      }}
                    />
                  </div>
                  <div className="date-details">
                    <span>{formatTime(data.totalTime)}</span>
                    <span>{data.taskCount} tasks</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
